import { energyAnalysisApiSlice } from '../apiSlice';

export const calculateEnergySavingsApiSlice =
  energyAnalysisApiSlice.injectEndpoints({
    endpoints: (builder) => ({
      calculateEnergySavings: builder.query({
        query: ({
          comparison_start_timestamp,
          comparison_end_timestamp,
          baseline_start_timestamp,
          baseline_end_timestamp,
          unit_id,
          unit_type_input_id,
          data_type,
        }) => {
          const params = new URLSearchParams({
            comparison_start_timestamp,
            comparison_end_timestamp,
            baseline_start_timestamp,
            baseline_end_timestamp,
            unit_id,
            unit_type_input_id,
            data_type,
          });
          return {
            url: `/energy_savings?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      calculateEnergySavingsMultipleDataStreams: builder.mutation({
        query: ({
          comparison_start_timestamp,
          comparison_end_timestamp,
          baseline_start_timestamp,
          baseline_end_timestamp,
          dataStreams, // This is the array of unit data streams
        }) => {
          const params = new URLSearchParams({
            comparison_start_timestamp,
            comparison_end_timestamp,
            baseline_start_timestamp,
            baseline_end_timestamp,
          });

          return {
            url: `/energy_savings?${params.toString()}`, // Append the query parameters
            method: 'POST',
            body: dataStreams, // Send the dataStreams array in the POST body
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      calculateBasicEnergySavingsMultipleDataStreams: builder.mutation({
        query: ({
          comparison_start_timestamp,
          comparison_end_timestamp,
          baseline_start_timestamp,
          baseline_end_timestamp,
          dataStreams, // This is the array of unit data streams
        }) => {
          const params = new URLSearchParams({
            comparison_start_timestamp,
            comparison_end_timestamp,
            baseline_start_timestamp,
            baseline_end_timestamp,
          });

          return {
            url: `/basic_energy_savings?${params.toString()}`, // Append the query parameters
            method: 'POST',
            body: dataStreams, // Send the dataStreams array in the POST body
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      extractEnergyData: builder.mutation({
        query: ({
          start_timestamp,
          end_timestamp,
          location_code,
          dataStreams, // This is the array of unit data streams
        }) => {
          const params = new URLSearchParams({
            start_timestamp,
            end_timestamp,
            location_code,
          });
          return {
            url: `/extract_energy_data?${params.toString()}`,
            method: 'POST',
            body: dataStreams, // Send the dataStreams array in the POST body
          };
        },
        invalidatesTags: ['EnergyAnalysis'],
      }),

      getEnergyOverviewData: builder.query({
        query: ({ accountId, energyGroupId, start, end }) => {
          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
          });
          return {
            url: `/energy_overview/${accountId}/${energyGroupId}?${params.toString()}`,
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      getEnergyConsumption: builder.query({
        query: (args) => {
          const {
            accountId,
            energyGroupId,
            nodeId,
            start,
            end,
            aggregate,
            activeHoursFilter, // Optional boolean
            activeDaysFilter, // Optional boolean
            activeHolidaysFilter, // Optional boolean
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
            aggregate_frequency: aggregate,
          });

          // Conditionally append parameters only if they are explicitly set (true or false)
          if (activeHoursFilter !== undefined) {
            params.append('active_hours_filter', activeHoursFilter.toString());
          }
          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/energy_consumption/${accountId}/${energyGroupId}/${nodeId}?${params.toString()}`,
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      getSeuEnergyConsumption: builder.query({
        query: (args) => {
          const {
            accountId,
            energyGroupId,
            seu,
            start,
            end,
            aggregate,
            activeHoursFilter, // Optional boolean
            activeDaysFilter, // Optional boolean
            activeHolidaysFilter, // Optional boolean
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
            aggregate_frequency: aggregate,
          });

          // Conditionally append parameters only if they are explicitly set (true or false)
          if (activeHoursFilter !== undefined) {
            params.append('active_hours_filter', activeHoursFilter.toString());
          }
          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/energy_consumption_seu/${accountId}/${energyGroupId}/${seu}?${params.toString()}`,
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      getChildAnalysis: builder.query({
        query: (args) => {
          const {
            accountId,
            energyGroupId,
            nodeId,
            start,
            end,
            activeHoursFilter, // Optional boolean
            activeDaysFilter, // Optional boolean
            activeHolidaysFilter, // Optional boolean
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
          });

          // Conditionally append parameters only if they are explicitly set (true or false)
          if (activeHoursFilter !== undefined) {
            params.append('active_hours_filter', activeHoursFilter.toString());
          }
          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/child_node_analysis/${accountId}/${energyGroupId}/${nodeId}?${params.toString()}`,
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      getDurationAnalysis: builder.query({
        query: (args) => {
          const {
            energyGroupId,
            accountId,
            nodeId,
            start,
            end,
            activeHoursFilter, // Optional boolean
            activeDaysFilter, // Optional boolean
            activeHolidaysFilter, // Optional boolean
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
          });

          // Conditionally append parameters only if they are explicitly set (true or false)
          if (activeHoursFilter !== undefined) {
            params.append('active_hours_filter', activeHoursFilter.toString());
          }
          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/duration_analysis/${accountId}/${energyGroupId}/${nodeId}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      getSeuDurationAnalysis: builder.query({
        query: (args) => {
          const {
            energyGroupId,
            accountId,
            seu,
            start,
            end,
            activeHoursFilter, // Optional boolean
            activeDaysFilter, // Optional boolean
            activeHolidaysFilter, // Optional boolean
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
          });

          // Conditionally append parameters only if they are explicitly set (true or false)
          if (activeHoursFilter !== undefined) {
            params.append('active_hours_filter', activeHoursFilter.toString());
          }
          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/duration_analysis_seu/${accountId}/${energyGroupId}/${seu}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      getHourAnalysis: builder.query({
        query: (args) => {
          const {
            energyGroupId,
            accountId,
            nodeId,
            start,
            end,
            activeDaysFilter,
            activeHolidaysFilter,
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
          });

          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/hour_analysis/${accountId}/${energyGroupId}/${nodeId}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      getSeuHourAnalysis: builder.query({
        query: (args) => {
          const {
            energyGroupId,
            accountId,
            seu,
            start,
            end,
            activeDaysFilter,
            activeHolidaysFilter,
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
          });

          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/hour_analysis_seu/${accountId}/${energyGroupId}/${seu}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),

      getCO2eEmissionData: builder.query({
        query: ({ accountId, energyGroupId, start, end }) => {
          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
          });
          return {
            url: `/co2e_emission_data/${accountId}/${energyGroupId}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['CO2eEmissionData'],
      }),
      getCO2eEmissionTimestampSummary: builder.query({
        query: ({ accountId, energyGroupId }) => {
          return {
            url: `/co2e_emission_timestamps_summary/${accountId}/${energyGroupId}`,
            method: 'GET',
          };
        },
        providesTags: ['CO2eEmissionData'],
      }),
      getStandardAnalysis: builder.query({
        query: ({ accountId, energyGroupId, start, end }) => {
          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end + 1).toISOString(), // Ensure ISO format
          });
          return {
            url: `/standard_analysis/${accountId}/${energyGroupId}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),
      getPowerLoadProfileAnalysis: builder.query({
        query: ({
          accountId,
          energyGroupId,
          nodeId,
          startTimestamp,
          endTimestamp,
          daysOfHistory = 28,
        }) => {
          const params = new URLSearchParams({
            start_timestamp: new Date(startTimestamp).toISOString(),
            end_timestamp: new Date(endTimestamp + 1).toISOString(),
            days_of_history: daysOfHistory,
          });
          return {
            url: `/power_load_profile_analysis/${accountId}/${energyGroupId}/${nodeId}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),
      getPowerLoadProfileAnalysisSeu: builder.query({
        query: ({
          accountId,
          energyGroupId,
          seuType,
          startTimestamp,
          endTimestamp,
          daysOfHistory = 28,
        }) => {
          const params = new URLSearchParams({
            start_timestamp: new Date(startTimestamp).toISOString(),
            end_timestamp: new Date(endTimestamp + 1).toISOString(),
            days_of_history: daysOfHistory,
          });
          return {
            url: `/power_load_profile_analysis_seu/${accountId}/${energyGroupId}/${seuType}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['EnergyAnalysis'],
      }),
      getEnergySavings: builder.query({
        query: ({ accountId, energyGroupId }) => {
          return {
            url: `/energy_savings/${accountId}/${energyGroupId}`,
            method: 'GET',
          };
        },
      }),
    }),
  });

export const {
  useCalculateEnergySavingsQuery,
  useCalculateEnergySavingsMultipleDataStreamsMutation,
  useCalculateBasicEnergySavingsMultipleDataStreamsMutation,
  useExtractEnergyDataMutation,
  useGetEnergyConsumptionQuery,
  useLazyGetEnergyConsumptionQuery,
  useLazyGetSeuEnergyConsumptionQuery,
  useGetEnergyOverviewDataQuery,
  useLazyGetEnergyOverviewDataQuery,
  useGetCO2eEmissionDataQuery,
  useGetCO2eEmissionTimestampSummaryQuery,
  useLazyGetDurationAnalysisQuery,
  useLazyGetSeuDurationAnalysisQuery,
  useLazyGetHourAnalysisQuery,
  useLazyGetSeuHourAnalysisQuery,
  useLazyGetChildAnalysisQuery,
  useGetStandardAnalysisQuery,
  useGetPowerLoadProfileAnalysisQuery,
  useLazyGetPowerLoadProfileAnalysisQuery,
  useGetPowerLoadProfileAnalysisSeuQuery,
  useLazyGetPowerLoadProfileAnalysisSeuQuery,
  useGetEnergySavingsQuery,
} = calculateEnergySavingsApiSlice;
